import { OPTIONS_DATA_START_DATETIME } from './constants.js';
const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const CANONICAL_ANCHOR_DATE = OPTIONS_DATA_START_DATETIME.slice(0, 10);
/**
 * Resolve every Alpha Atlas reporting and scientific boundary from explicit
 * inputs. The helper never reads the wall clock and never manufactures a
 * trading day; callers must persist the returned boundaries with the admitted
 * plan or campaign.
 */
export function resolveAlphaAtlasTimeBoundaries(input) {
    validateInput(input);
    const sessions = [...input.marketCalendar].sort((left, right) => left.decisionAtUnix - right.decisionAtUnix || left.tradingDate.localeCompare(right.tradingDate));
    const eligible = sessions.filter((session) => session.tradingDate >= CANONICAL_ANCHOR_DATE);
    if (eligible.length === 0)
        throw new Error('Alpha Atlas market calendar has no session on or after the canonical anchor');
    const safeCutoff = input.asOfAtUnix - input.safetyPolicy.sourceLagSeconds;
    const safeSessions = eligible.filter((session) => session.completedAtUnix <= input.asOfAtUnix && session.sourceAvailableAtUnix <= safeCutoff);
    if (safeSessions.length === 0)
        throw new Error('Alpha Atlas market calendar has no source-safe completed session');
    const historicalBoundaryRaw = subtractCalendarMonths(input.asOfDate, 12);
    const verificationBoundaryRaw = subtractCalendarMonths(input.asOfDate, 6);
    const historicalBoundary = firstSessionOnOrAfter(eligible, historicalBoundaryRaw);
    const verificationBoundary = firstSessionOnOrAfter(eligible, verificationBoundaryRaw);
    if (!historicalBoundary || !verificationBoundary) {
        throw new Error('Alpha Atlas market calendar does not cover the twelve- and six-month boundaries');
    }
    const latestSafe = safeSessions.at(-1);
    const firstEligible = eligible[0];
    const horizons = input.horizons
        .map((horizon) => resolveHorizonBoundaries({
        horizon,
        safeSessions,
        firstEligible,
        historicalBoundary,
        verificationBoundary
    }))
        .sort((left, right) => left.horizonSeconds - right.horizonSeconds);
    return {
        duration: input.duration,
        asOfDate: input.asOfDate,
        asOfAtUnix: input.asOfAtUnix,
        anchorDateTime: OPTIONS_DATA_START_DATETIME,
        firstEligibleSessionAtUnix: firstEligible.decisionAtUnix,
        firstEligibleTradingDate: firstEligible.tradingDate,
        latestSafeSessionAtUnix: latestSafe.decisionAtUnix,
        latestSafeTradingDate: latestSafe.tradingDate,
        historicalBoundaryTradingDate: historicalBoundary.tradingDate,
        verificationBoundaryTradingDate: verificationBoundary.tradingDate,
        horizons
    };
}
function resolveHorizonBoundaries(input) {
    const { horizon, safeSessions, firstEligible, historicalBoundary, verificationBoundary } = input;
    const matureEnd = resolveMatureDecisionEnd(safeSessions, horizon.maturity);
    return {
        horizonSeconds: horizon.horizonSeconds,
        matureDecisionEndAtUnix: matureEnd?.decisionAtUnix ?? null,
        matureDecisionEndTradingDate: matureEnd?.tradingDate ?? null,
        wholeRange: inclusiveWindow(firstEligible, matureEnd),
        historicalTraining: boundedWindow(firstEligible, previousSession(safeSessions, historicalBoundary), matureEnd),
        recentReporting: boundedWindow(historicalBoundary, matureEnd, matureEnd),
        selectionValidation: boundedWindow(historicalBoundary, previousSession(safeSessions, verificationBoundary), matureEnd),
        verification: boundedWindow(verificationBoundary, matureEnd, matureEnd)
    };
}
function resolveMatureDecisionEnd(safeSessions, maturity) {
    const latestSafe = safeSessions.at(-1);
    if (!latestSafe)
        return undefined;
    if (maturity.kind === 'tradingSessions')
        return safeSessions.at(-(maturity.count + 1));
    const latestDecisionAtUnix = latestSafe.completedAtUnix - maturity.count;
    return [...safeSessions].reverse().find((session) => session.decisionAtUnix <= latestDecisionAtUnix);
}
function boundedWindow(requestedStart, requestedEnd, matureEnd) {
    if (!requestedEnd || !matureEnd)
        return emptyWindow();
    const end = requestedEnd.decisionAtUnix <= matureEnd.decisionAtUnix ? requestedEnd : matureEnd;
    return inclusiveWindow(requestedStart, end);
}
function inclusiveWindow(start, end) {
    if (!start || !end || start.decisionAtUnix > end.decisionAtUnix)
        return emptyWindow();
    return {
        startAtUnix: start.decisionAtUnix,
        endAtUnix: end.decisionAtUnix,
        startTradingDate: start.tradingDate,
        endTradingDate: end.tradingDate
    };
}
function emptyWindow() {
    return {
        startAtUnix: null,
        endAtUnix: null,
        startTradingDate: null,
        endTradingDate: null
    };
}
function firstSessionOnOrAfter(sessions, tradingDate) {
    return sessions.find((session) => session.tradingDate >= tradingDate);
}
function previousSession(sessions, boundary) {
    const index = sessions.findIndex((session) => session.decisionAtUnix === boundary.decisionAtUnix);
    return index > 0 ? sessions[index - 1] : undefined;
}
function subtractCalendarMonths(ymd, months) {
    const [year, month, day] = ymd.split('-').map(Number);
    const absoluteMonth = year * 12 + month - 1 - months;
    const targetYear = Math.floor(absoluteMonth / 12);
    const targetMonth = (absoluteMonth % 12) + 1;
    const targetDay = Math.min(day, daysInMonth(targetYear, targetMonth));
    return `${String(targetYear).padStart(4, '0')}-${String(targetMonth).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
}
function daysInMonth(year, month) {
    if (month === 2) {
        const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
        return leapYear ? 29 : 28;
    }
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
}
function validateInput(input) {
    if (!YMD_PATTERN.test(input.asOfDate) || !Number.isSafeInteger(input.asOfAtUnix) || input.asOfAtUnix <= 0) {
        throw new Error('Alpha Atlas as-of date/time is invalid');
    }
    if (!Number.isSafeInteger(input.safetyPolicy.sourceLagSeconds) || input.safetyPolicy.sourceLagSeconds < 0) {
        throw new Error('Alpha Atlas source safety lag must be a non-negative integer');
    }
    if (input.marketCalendar.length === 0)
        throw new Error('Alpha Atlas market calendar is empty');
    if (input.horizons.length === 0)
        throw new Error('Alpha Atlas horizon set is empty');
    const dates = new Set();
    const decisions = new Set();
    for (const session of input.marketCalendar) {
        if (!YMD_PATTERN.test(session.tradingDate) ||
            !Number.isSafeInteger(session.decisionAtUnix) ||
            !Number.isSafeInteger(session.completedAtUnix) ||
            !Number.isSafeInteger(session.sourceAvailableAtUnix) ||
            session.decisionAtUnix <= 0 ||
            session.decisionAtUnix > session.completedAtUnix ||
            session.completedAtUnix > session.sourceAvailableAtUnix ||
            dates.has(session.tradingDate) ||
            decisions.has(session.decisionAtUnix)) {
            throw new Error('Alpha Atlas market calendar contains an invalid or duplicate session');
        }
        dates.add(session.tradingDate);
        decisions.add(session.decisionAtUnix);
    }
    const horizonIds = new Set();
    for (const horizon of input.horizons) {
        if (!Number.isSafeInteger(horizon.horizonSeconds) ||
            horizon.horizonSeconds <= 0 ||
            !Number.isSafeInteger(horizon.maturity.count) ||
            horizon.maturity.count <= 0 ||
            horizonIds.has(horizon.horizonSeconds)) {
            throw new Error('Alpha Atlas horizons must have unique positive integer identities and maturity counts');
        }
        horizonIds.add(horizon.horizonSeconds);
    }
}
//# sourceMappingURL=alphaAtlasTimePolicy.js.map